/**
 * AddParaItemModal - Modal for adding/editing PARA items in Second Brain
 * Modern UI with category cards, priority pills, and markdown preview
 */

import { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import MicButton from "./MicButton";
import "../../assets/css/sb-modal.css";

const CATEGORIES = [
  { id: "project", label: "Project", icon: "bi-rocket-takeoff-fill", description: "Active goals with deadlines" },
  { id: "area", label: "Area", icon: "bi-bullseye", description: "Ongoing responsibilities" },
  { id: "resource", label: "Resource", icon: "bi-book-fill", description: "Reference materials" },
  { id: "archive", label: "Archive", icon: "bi-archive-fill", description: "Completed/inactive items" },
];

const PRIORITIES = [
  { id: "low", label: "Low", color: "#6c757d" },
  { id: "medium", label: "Medium", color: "#ffc107" },
  { id: "high", label: "High", color: "#dc3545" },
];

export default function AddParaItemModal({ show, onClose, onSave, editItem }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("resource");
  const [priority, setPriority] = useState("medium");
  const [tags, setTags] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Populate form when editing - use verbatim (original) content first
  useEffect(() => {
    if (editItem) {
      setTitle(editItem.title || "");
      // Prefer verbatim (original full content) over summary/content
      setContent(editItem.verbatim || editItem.content || "");
      setCategory(editItem.category || "resource");
      setPriority(editItem.priority || "medium");
      setTags(editItem.tags?.join(", ") || "");
    } else {
      resetForm();
    }
  }, [editItem, show]);

  const resetForm = () => {
    setTitle("");
    setContent("");
    setCategory("resource");
    setPriority("medium");
    setTags("");
    setError(null);
    setShowPreview(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      setError("Please enter a title");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const itemData = {
        title: title.trim(),
        content: content.trim(),
        category,
        priority,
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t.length > 0),
      };

      await onSave(itemData);
      resetForm();
    } catch {
      setError("Failed to save item");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" className="sb-modal">
      <Modal.Header closeButton className="sb-modal-header">
        <Modal.Title className="sb-modal-title">
          <i className="bi bi-journal-bookmark-fill me-2"></i>
          {editItem ? "Edit Item" : "Add to Second Brain"}
        </Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="sb-modal-body">
          {error && (
            <div className="sb-error-alert">
              <i className="bi bi-exclamation-circle me-2"></i>
              {error}
            </div>
          )}

          {/* Title */}
          <div className="sb-form-group">
            <label className="sb-label">Title <span className="required">*</span></label>
            <input
              type="text"
              className="sb-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Machine Learning Notes, Health Goals..."
              disabled={saving}
              autoFocus
            />
          </div>

          {/* Category Cards */}
          <div className="sb-form-group">
            <label className="sb-label">Category</label>
            <div className="sb-category-selector">
              {CATEGORIES.map((cat) => (
                <div
                  key={cat.id}
                  className={`sb-category-card ${category === cat.id ? 'active' : ''}`}
                  onClick={() => !saving && setCategory(cat.id)}
                >
                  <i className={`bi ${cat.icon} sb-category-icon`}></i>
                  <div className="sb-category-label">{cat.label}</div>
                  <div className="sb-category-desc">{cat.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Content with Preview Toggle */}
          <div className="sb-form-group">
            <div className="sb-label-row">
              <label className="sb-label">Content</label>
              <button
                type="button"
                className={`sb-preview-toggle ${showPreview ? 'active' : ''}`}
                onClick={() => setShowPreview(!showPreview)}
              >
                <i className={`bi ${showPreview ? 'bi-pencil' : 'bi-eye'} me-1`}></i>
                {showPreview ? 'Edit' : 'Preview'}
              </button>
            </div>
            
            {showPreview ? (
              <div className="sb-content-preview">
                {content ? (
                  <ReactMarkdown>{content}</ReactMarkdown>
                ) : (
                  <span className="sb-preview-empty">Nothing to preview</span>
                )}
              </div>
            ) : (
              <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                <textarea
                  className="sb-textarea"
                  rows={5}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Add notes, details, or markdown content..."
                  disabled={saving}
                  style={{ flex: 1 }}
                />
                <MicButton
                  onTranscript={(text) => setContent(prev => prev ? `${prev} ${text}` : text)}
                  disabled={saving}
                  size="md"
                />
              </div>
            )}
            <div className="sb-hint">Supports markdown: **bold**, *italic*, # headers, - lists</div>
          </div>

          {/* Priority Pills */}
          <div className="sb-form-group">
            <label className="sb-label">Priority</label>
            <div className="sb-priority-selector">
              {PRIORITIES.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`sb-priority-btn ${priority === p.id ? 'active' : ''}`}
                  style={{ 
                    '--priority-color': p.color,
                    borderColor: priority === p.id ? p.color : undefined,
                    backgroundColor: priority === p.id ? `${p.color}20` : undefined,
                  }}
                  onClick={() => !saving && setPriority(p.id)}
                  disabled={saving}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className="sb-form-group">
            <label className="sb-label">Tags</label>
            <input
              type="text"
              className="sb-input"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="work, personal, learning... (comma-separated)"
              disabled={saving}
            />
          </div>
        </Modal.Body>

        <Modal.Footer className="sb-modal-footer">
          <Button 
            variant="secondary" 
            onClick={handleClose} 
            disabled={saving}
            className="sb-btn-cancel"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={saving || !title.trim()}
            className="sb-btn-save"
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <i className={`bi ${editItem ? "bi-check-lg" : "bi-plus-lg"} me-2`}></i>
                {editItem ? "Save Changes" : "Add Item"}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
